// A dialog the console owns, on the design's modal (TASKS §143, step 5).
//
// There was none. Add-person lived inline under the list, so the list and the
// form were one card and a person already on the books looked like a leftover
// from the form. One component, used wherever a write wants a moment of its
// own, so the list stays a list.
//
// THE DESIGN'S MARKUP AND THE KIT'S BEHAVIOUR. `.modal` over a scrim button,
// `.modal-box` with its title and a Close, the body, and the actions at the
// foot in `.modal-acts`, Cancel before the verb, as desk-ui's Confirm draws
// them. useDialog is the kit's: focus goes into the box and stays there, Escape
// closes it, and focus goes back where it came from. A click on the scrim
// closes it; a click inside the box does not, because that is somebody using
// a field.
//
// FOCUS THAT FALLS OUT OF THE BOX IS BROUGHT BACK. useDialog listens on the box,
// and pressing a write disables the button that was focused, which drops focus
// to the page. So Escape is heard on the document as well, and Tab from outside
// the box goes back into it: a save that failed must not leave a keyboard on
// the page behind a dialog that is still open. Found by Bugbot on #146.
//
// THE PAGE BEHIND HOLDS STILL. The stage is the page's scroll container, so it
// is held while a dialog is open; otherwise a wheel over the scrim moves the
// page underneath.
//
// THE BODY IS A FORM'S, NOT A CONFIRM'S. `.modal-body` styles every paragraph in
// it as a confirm's small print, which would restyle a refusal's sentence and
// View Live's preview, so the body here is `.modal-form`: the same place under
// the title and nothing done to what is in it.
//
// THE FORM STAYS MOUNTED UNDERNEATH. Closing puts the person back where they
// were.

import { useEffect, useId, useRef, type ReactNode } from "react";
import { useDialog } from "@nova/desk-ui/states";

export type DialogProps = {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** The actions, drawn at the foot: Cancel first, the verb last. */
  readonly footer?: ReactNode;
  /**
   * A preview that has to look like a page, not a form. The default box is
   * a short write; View Live needs room for a phone frame without the frame
   * becoming a skinny column in a box that was sized for a name field.
   */
  readonly wide?: boolean;
};

export function Dialog({ title, onClose, children, footer, wide }: DialogProps): ReactNode {
  const titleId = useId();
  const box = useRef<HTMLDivElement | null>(null);
  useDialog(box, onClose);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const node = box.current;
      if (node === null || node.contains(document.activeElement)) return;
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "Tab") {
        const first = node.querySelector<HTMLElement>(
          'button:not(:disabled),[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled)',
        );
        event.preventDefault();
        (first ?? node).focus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.documentElement.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.documentElement.classList.remove("modal-open");
    };
  }, [onClose]);

  return (
    <div className="modal">
      <button type="button" className="scrim-bd" aria-label="Close" tabIndex={-1} onClick={onClose} />
      <div
        className={wide === true ? "modal-box modal-wide" : "modal-box"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={box}
      >
        <div className="head-bar">
          <h2 className="modal-t" id={titleId}>
            {title}
          </h2>
          <button type="button" className="btn btn-quiet btn-sm" onClick={onClose} aria-label={`Close ${title}`}>
            Close
          </button>
        </div>
        <div className="modal-form">{children}</div>
        {footer === undefined ? null : <div className="acts modal-acts">{footer}</div>}
      </div>
    </div>
  );
}
